import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, PRIMARY_OUTLET, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap } from 'rxjs/operators';
import { ActivityActions } from '../activities/activities.actions';
import { AssignmentActions } from '../assignments/assignments.actions';
import { AuthActions } from '../auth/auth.actions';
import { ClientActions } from '../clients/clients.actions';
import { ClientTypesActions } from '../clientTypes/clientTypes.actions';
import { FileVaultActions } from '../filevault/filevault.actions';
import { FirmActions } from '../firm/firm.actions';
import { TaskGroupsActions } from '../task-groups/task-groups.actions';
import { TeamsActions } from '../teams/teams.actions';
import { UserActions } from '../users/users.actions';
import { CoreActions } from './core.actions';
import { AnalyticRouteData } from '../../entity/analytics/analytic-route-data';
import { AnalyticService } from '../../entity/analytics/analytic-tracking';

@Injectable()
export class CoreEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private analyticService = inject(AnalyticService);

  initCoreEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadCurrentUserSuccessful),
      switchMap(() => [
        TeamsActions.loadAllTeams(),
        UserActions.loadAllUsers(),
        AssignmentActions.loadAllAssignments(),
        ClientActions.loadAllClients(),
        FirmActions.loadFirm(),
        ActivityActions.loadAllActivityStates(),
        FileVaultActions.loadAllFileNames(),
        ClientTypesActions.loadAllClientTypes(),
        TaskGroupsActions.load(),
      ]),
    ),
  );

  navigateStart$ = createEffect(() =>
    this.router.events.pipe(
      filter((event) => event instanceof NavigationStart),
      map(({ url }: NavigationStart) => CoreActions.navigationStart({ url })),
    ),
  );

  navigationEnd$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => this.extractAnalyticData(this.router.routerState.snapshot.root, event)),
        map(({ pageTitle, url }) => this.analyticService.trackPageView(pageTitle, url)),
      ),
    { dispatch: false },
  );

  private getLeafRoute(route: ActivatedRouteSnapshot, outlet: string = PRIMARY_OUTLET): ActivatedRouteSnapshot {
    const child = this.findChildRoute(route, outlet);
    return child ? this.getLeafRoute(child, outlet) : route;
  }

  private findChildRoute(route: ActivatedRouteSnapshot, outlet: string): ActivatedRouteSnapshot | undefined {
    return route.children.find((child) => child.outlet === outlet);
  }

  private extractAnalyticData(route: ActivatedRouteSnapshot, event: NavigationEnd): { pageTitle: string; url: string } {
    const routeData = this.getLeafRoute(route)?.data || ({} as AnalyticRouteData);
    const pageTitle = routeData.analytics?.title || 'No Title';
    const url = event.urlAfterRedirects;
    return { pageTitle, url };
  }
}
